import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { usePlatformAuth } from '../../context/PlatformAuthContext';
import PlatformLayout from '../../components/layout/PlatformLayout';
import './Platform.css';
import { TrendingUp, Building2, Users, ShoppingBag, Globe, ChevronDown, ChevronRight } from 'lucide-react';

const PERIODS = [
  { label: '7 days', value: '7d' },
  { label: '30 days', value: '30d' },
  { label: '90 days', value: '90d' },
  { label: '1 year', value: '1y' },
  { label: 'All time', value: 'all' }
];

const SCOPE_ICONS = {
  local: '📍', regional: '🗺️', national: '🏴', international: '🌍'
};

const TIER_COLORS = {
  platinum: '#6B6B8A', gold: '#C4943A', silver: '#888', standard: '#1E4A35'
};

export default function PlatformReports() {
  const { platformAxios } = usePlatformAuth();
  const [activeTab, setActiveTab] = useState('sponsor');
  const [period, setPeriod] = useState('30d');
  const [expandedSponsorId, setExpandedSponsorId] = useState(null);

  const { data: sponsorReport, isLoading: sponsorLoading } = useQuery({
    queryKey: ['report-by-sponsor', period],
    queryFn: () => platformAxios.get(`/reports/by-sponsor?period=${period}`).then(r => r.data)
  });

  const { data: clubReport, isLoading: clubLoading } = useQuery({
    queryKey: ['report-by-club', period],
    queryFn: () => platformAxios.get(`/reports/by-club?period=${period}`).then(r => r.data)
  });

  const { data: sponsorDetail } = useQuery({
    queryKey: ['sponsor-clubs', expandedSponsorId],
    queryFn: () => platformAxios.get(`/sponsors/${expandedSponsorId}/clubs`).then(r => r.data),
    enabled: !!expandedSponsorId
  });

  const fmtGBP = (val) => `£${parseFloat(val || 0).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  return (
    <PlatformLayout>
      <div className="platform-page">
        <div className="page-header">
          <div>
            <h1>Attribution Reports</h1>
            <p>Measure sponsor performance across all clubs on the platform</p>
          </div>
          {/* Period selector */}
          <div className="period-selector">
            {PERIODS.map(p => (
              <button
                key={p.value}
                className={`period-btn ${period === p.value ? 'active' : ''}`}
                onClick={() => setPeriod(p.value)}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Summary cards — always show combined totals */}
        <div className="analytics-grid">
          <div className="analytics-card">
            <div className="card-icon building"><Building2 size={28} /></div>
            <div className="card-content">
              <p className="card-label">Sponsors Tracked</p>
              <p className="card-value">{sponsorReport?.summary?.totalSponsors ?? '—'}</p>
            </div>
          </div>
          <div className="analytics-card">
            <div className="card-icon redemptions"><ShoppingBag size={28} /></div>
            <div className="card-content">
              <p className="card-label">Redemptions</p>
              <p className="card-value">{sponsorReport?.summary?.totalRedemptions ?? '—'}</p>
            </div>
          </div>
          <div className="analytics-card">
            <div className="card-icon users"><Users size={28} /></div>
            <div className="card-content">
              <p className="card-label">Unique Members</p>
              <p className="card-value">{sponsorReport?.summary?.totalUniqueMembers ?? '—'}</p>
            </div>
          </div>
          <div className="analytics-card">
            <div className="card-icon sponsors"><TrendingUp size={28} /></div>
            <div className="card-content">
              <p className="card-label">Attributed Value</p>
              <p className="card-value">{sponsorReport ? fmtGBP(sponsorReport.summary.totalValue) : '—'}</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="report-tabs">
          <button
            className={`report-tab ${activeTab === 'sponsor' ? 'active' : ''}`}
            onClick={() => setActiveTab('sponsor')}
          >
            <Building2 size={16} /> By Advertiser / Sponsor
          </button>
          <button
            className={`report-tab ${activeTab === 'club' ? 'active' : ''}`}
            onClick={() => setActiveTab('club')}
          >
            <Globe size={16} /> By Club
          </button>
        </div>

        {/* ─── BY SPONSOR TAB ─── */}
        {activeTab === 'sponsor' && (
          <div className="report-section">
            {sponsorLoading ? (
              <div className="loading-state"><div className="spinner"></div><p>Loading report...</p></div>
            ) : !sponsorReport?.sponsors?.length ? (
              <div className="empty-state">
                <TrendingUp size={48} />
                <h3>No data yet</h3>
                <p>Attribution data will appear here once sponsors are linked to clubs and members start redeeming offers.</p>
              </div>
            ) : (
              <div className="report-table-wrap">
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Sponsor</th>
                      <th>Category</th>
                      <th>Scope</th>
                      <th>Clubs</th>
                      <th>Redemptions</th>
                      <th>Unique Members</th>
                      <th>Attributed Value</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sponsorReport.sponsors.map(s => {
                      const isExpanded = expandedSponsorId === s.id;
                      return (
                        <React.Fragment key={s.id}>
                          <tr
                            className={`report-row ${!s.is_active ? 'row-inactive' : ''} ${isExpanded ? 'row-expanded' : ''}`}
                            onClick={() => setExpandedSponsorId(isExpanded ? null : s.id)}
                          >
                            <td>
                              <div className="sponsor-cell">
                                {s.logo_url ? (
                                  <img src={s.logo_url} alt={s.business_name} className="report-sponsor-logo" />
                                ) : (
                                  <div className="report-sponsor-logo-placeholder"><Building2 size={14} /></div>
                                )}
                                <span>{s.business_name}</span>
                                {!s.is_active && <span className="badge-inactive">Inactive</span>}
                              </div>
                            </td>
                            <td>{s.category || '—'}</td>
                            <td>{s.geographic_scope ? <>{SCOPE_ICONS[s.geographic_scope]} {s.geographic_scope}</> : '—'}</td>
                            <td>{s.clubs_sponsored}</td>
                            <td><strong>{s.redemptions}</strong></td>
                            <td>{s.unique_members}</td>
                            <td><strong>{fmtGBP(s.total_value)}</strong></td>
                            <td>
                              {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr className="report-detail-row">
                              <td colSpan={8}>
                                <div className="report-detail-panel">
                                  <h4>Club breakdown for {s.business_name}</h4>
                                  {!sponsorDetail ? (
                                    <p className="loading-text">Loading...</p>
                                  ) : sponsorDetail.length === 0 ? (
                                    <p className="empty-text">Not linked to any clubs yet.</p>
                                  ) : (
                                    <table className="inner-table">
                                      <thead>
                                        <tr>
                                          <th>Club</th>
                                          <th>Sport</th>
                                          <th>Location</th>
                                          <th>Tier</th>
                                          <th>Deal Value</th>
                                          <th>Redemptions</th>
                                          <th>Attributed Value</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {sponsorDetail.map(link => (
                                          <tr key={link.link_id}>
                                            <td><strong>{link.club_name}</strong></td>
                                            <td>{link.sport || '—'}</td>
                                            <td>{link.location || '—'}</td>
                                            <td>
                                              <span className="tier-badge" style={{ borderColor: TIER_COLORS[link.tier], color: TIER_COLORS[link.tier] }}>
                                                {link.tier}
                                              </span>
                                            </td>
                                            <td>{link.deal_value ? `${link.currency || '£'}${parseFloat(link.deal_value).toLocaleString()}` : '—'}</td>
                                            <td>{link.redemptions || 0}</td>
                                            <td>{fmtGBP(link.total_value)}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="report-totals">
                      <td colSpan={4}><strong>Totals</strong></td>
                      <td><strong>{sponsorReport.summary.totalRedemptions}</strong></td>
                      <td><strong>{sponsorReport.summary.totalUniqueMembers}</strong></td>
                      <td><strong>{fmtGBP(sponsorReport.summary.totalValue)}</strong></td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ─── BY CLUB TAB ─── */}
        {activeTab === 'club' && (
          <div className="report-section">
            {clubLoading ? (
              <div className="loading-state"><div className="spinner"></div><p>Loading report...</p></div>
            ) : !clubReport?.clubs?.length ? (
              <div className="empty-state">
                <Globe size={48} />
                <h3>No club data yet</h3>
                <p>Clubs with active sponsors and redemption activity will appear here.</p>
              </div>
            ) : (
              <div className="report-table-wrap">
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Club</th>
                      <th>Sport</th>
                      <th>Location</th>
                      <th>Active Sponsors</th>
                      <th>Redemptions</th>
                      <th>Unique Members</th>
                      <th>Attributed Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clubReport.clubs.map(c => (
                      <tr key={c.club_id} className="report-row">
                        <td><strong>{c.club_name}</strong></td>
                        <td>{c.sport || '—'}</td>
                        <td>{c.location || '—'}</td>
                        <td>{c.active_sponsors}</td>
                        <td><strong>{c.redemptions}</strong></td>
                        <td>{c.unique_members}</td>
                        <td><strong>{fmtGBP(c.total_value)}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="report-totals">
                      <td colSpan={4}><strong>Totals</strong></td>
                      <td><strong>{clubReport.summary.totalRedemptions}</strong></td>
                      <td></td>
                      <td><strong>{fmtGBP(clubReport.summary.totalValue)}</strong></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </PlatformLayout>
  );
}
